import { ExceptionEvent, FaultData } from "@fairfleet/geotab";
import BuildIcon from "@mui/icons-material/Build";
import CarCrashIcon from "@mui/icons-material/CarCrash";
import ErrorIcon from "@mui/icons-material/Error";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import React, { Dispatch, JSX, SetStateAction, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import AccidentDetail from "../accidentDetail/AccidentDetail";
import { FaultDetail } from "./FaultDetail";
import PMDetail from "./PMDetail";
import TellTaleWarningDetails from "./TellTaleWarningDetails";
import { useDataGridSettings } from "@/components/DataGridSettingsProvider";
import AddInIds from "@/constants/AddInIds";
import { useAddinData } from "@/hooks/addins/useAddinData";
import Warning, { WarningType } from "@/models/Warning";
import RulesAddinData from "@/pages/pmrules-edit/models/RulesAddinData";
import SeatbeltSvg from "@assets/seatbelt.svg";

interface Props {
  warnings: Warning[];
  selectedFault: FaultData | null;
  setSelectedFault: Dispatch<SetStateAction<FaultData | null>>;
  deviceGroupIds?: string[];
}

type ExtWarningType = WarningType | "fault";
export type ExtWarning = Warning & { type: ExtWarningType; event?: FaultData | ExceptionEvent };

export const warningIcons: Record<ExtWarningType, JSX.Element> = {
  [WarningType.AD]: <CarCrashIcon color="error" />,
  [WarningType.PM]: <BuildIcon color="info" />,
  [WarningType.PM1]: <BuildIcon color="error" />,
  [WarningType.TT_RED]: <WarningAmberIcon color="error" />,
  [WarningType.TT_YELLOW]: <WarningAmberIcon color="warning" />,
  [WarningType.SB]: (
    <img
      src={SeatbeltSvg}
      alt="Seatbelt"
      style={{
        height: "24px",
        width: "24px",
      }}
    />
  ),
  ["fault"]: <ErrorIcon color="error" />,
};

const FaultInstructions: React.FC<Props> = ({ warnings, selectedFault, deviceGroupIds = [] }) => {
  const [activeTab, setActiveTab] = React.useState<ExtWarningType | null>(null);
  const { pageType } = useDataGridSettings();

  const groupedWarnings: Record<ExtWarningType, ExtWarning[]> = useMemo(() => {
    const res = warnings.reduce(
      (acc, warning) => {
        // ignore seatbelt warning for details
        if (warning.type === WarningType.SB) return acc;
        if (!acc[warning.type]) {
          acc[warning.type] = [];
        }
        acc[warning.type].push(warning);
        return acc;
      },
      {} as Record<ExtWarningType, ExtWarning[]>,
    );
    if (selectedFault) {
      res["fault"] = [
        {
          type: "fault",
          event: selectedFault,
        } as ExtWarning,
      ];
    }
    return res;
  }, [selectedFault, warnings]);

  const { data: pmAddinData = [] } = useAddinData<RulesAddinData>(
    AddInIds.pmrulesData,
    pageType === "statusMonitor",
  );

  const { t } = useTranslation();

  const renderWarningContent = useCallback(
    (warning: ExtWarning) => {
      const dId = warning.event?.diagnostic?.id;
      const rId = warning.event?.rule?.id;

      switch (warning.type as ExtWarningType) {
        case "fault":
          if (!dId || !warning.event) return null;
          return (
            <FaultDetail
              event={warning.event as FaultData}
              deviceGroupIds={deviceGroupIds}
            />
          );

        case WarningType.PM1:
        case WarningType.PM:
          if (!rId) return null;
          const addin2 = pmAddinData.find(d => d.ruleId === rId);
          return <PMDetail warning={warning} addin={addin2} />;

        case WarningType.TT_YELLOW:
          return warning.event ? (
            <TellTaleWarningDetails
              event={warning.event}
              color="warning"
              title={t("faultOverview.yellowTellTale")}
            />
          ) : null;

        case WarningType.TT_RED:
          return warning.event ? (
            <TellTaleWarningDetails
              event={warning.event}
              color="error"
              title={t("faultOverview.redTellTale")}
            />
          ) : null;

        case WarningType.AD:
          return <AccidentDetail event={warning.event as ExceptionEvent} />;

        default:
          return null;
      }
    },
    [pmAddinData, t, deviceGroupIds],
  );

  useEffect(() => {
    const warningTypes = Object.keys(groupedWarnings) as ExtWarningType[];
    if (warningTypes.length > 0 && activeTab === null) {
      setActiveTab(warningTypes[0]);
    }
  }, [groupedWarnings, activeTab]);

  useEffect(() => {
    if (selectedFault) setActiveTab("fault");
  }, [selectedFault]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        gap: 2,
        height: "35vh",
        overflow: "scroll",
        width: "100%",
      }}
    >
      <Tabs
        value={activeTab || false}
        onChange={(_, newValue) => setActiveTab(newValue)}
        variant="scrollable"
        orientation="vertical"
        scrollButtons={false}
        sx={{ minWidth: "50px" }}
      >
        {Object.keys(groupedWarnings).map(type => (
          <Tab
            key={type}
            value={type}
            icon={warningIcons[type as ExtWarningType]}
            sx={{ justifyContent: "center", alignItems: "center" }}
          />
        ))}
      </Tabs>

      {activeTab && groupedWarnings[activeTab] && (
        <Box sx={{ width: "calc(80% - 50px)" }}>
          {groupedWarnings[activeTab].map((warning, index) => (
            <Box key={index}>{renderWarningContent(warning)}</Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default FaultInstructions;
